import React, { useState, useEffect, useRef } from 'react';
import { Send, RotateCcw, User, Bot, AlertCircle, Sparkles } from 'lucide-react';
import { useBusinessId } from '../hooks/use-business-id';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  insights?: {
    sentiment: string;
    intent: string;
    stage: string;
    next_action: string;
  };
}

interface ConversationSummary {
  message_count: number;
  customer_messages: number;
  rex_messages: number;
  current_stage: string;
  duration: string;
}

export default function RexTesting() {
  const businessId = useBusinessId();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationSummary, setConversationSummary] = useState<ConversationSummary | null>(null);
  const [customerName, setCustomerName] = useState('');
  const [showInsights, setShowInsights] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Use the same URL as Nexus - direct to Railway
  const AI_SERVICE_URL = 'https://booking-app-production-d357.up.railway.app';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage: Message = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await fetch(`${AI_SERVICE_URL}/rex/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: inputMessage,
          conversation_id: 'test-session',
          customer_name: customerName || undefined,
          business_id: businessId || 'test',
          reset_conversation: false
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response from Rex');
      }

      const data = await response.json();

      const rexMessage: Message = {
        id: `msg-${Date.now()}-rex`,
        role: 'assistant',
        content: data.response,
        timestamp: new Date(),
        insights: data.insights
      };

      setMessages(prev => [...prev, rexMessage]);
      setConversationSummary(data.conversation_summary);

    } catch (error) {
      console.error('Error talking to Rex:', error);
      const errorMessage: Message = {
        id: `msg-${Date.now()}-error`,
        role: 'assistant',
        content: 'Sorry, I\'m having technical difficulties. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const resetConversation = async () => {
    try {
      await fetch(`${AI_SERVICE_URL}/rex/reset/test-session`, {
        method: 'POST'
      });
      setMessages([]);
      setConversationSummary(null);
    } catch (error) {
      console.error('Error resetting conversation:', error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Test scenarios for quick testing
  const testScenarios = [
    { label: 'Price Inquiry', message: 'How much do your services cost?' },
    { label: 'Urgent Need', message: 'I need help ASAP! Can someone come today?' },
    { label: 'Comparison', message: 'Why should I choose you over competitors?' },
    { label: 'Trust Building', message: 'How do I know you\'re reliable?' },
    { label: 'Booking Ready', message: 'I\'d like to schedule a service' }
  ];

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <Bot className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Rex Testing Interface</h1>
              <p className="text-sm text-gray-500">Test Rex's sales and customer service abilities</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <label className="flex items-center space-x-2 text-sm">
              <input
                type="checkbox"
                checked={showInsights}
                onChange={(e) => setShowInsights(e.target.checked)}
                className="rounded text-purple-600"
              />
              <span>Show Insights</span>
            </label>
            <button
              onClick={resetConversation}
              className="flex items-center space-x-2 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              <RotateCcw className="h-4 w-4" />
              <span>Reset</span>
            </button>
          </div>
        </div>
      </div>

      {/* Customer Name Input */}
      <div className="bg-white border-b px-6 py-3">
        <div className="flex items-center space-x-3">
          <label className="text-sm font-medium text-gray-700">Customer Name:</label>
          <input
            type="text"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            placeholder="Optional - Rex will use this in responses"
            className="flex-1 max-w-xs px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
      </div>

      {/* Test Scenarios */}
      <div className="bg-white border-b px-6 py-3">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-700">Quick Tests:</span>
          {testScenarios.map((scenario) => (
            <button
              key={scenario.label}
              onClick={() => setInputMessage(scenario.message)}
              className="px-2.5 py-1 text-xs bg-purple-50 text-purple-700 rounded-md hover:bg-purple-100"
            >
              {scenario.label}
            </button>
          ))}
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <Sparkles className="h-12 w-12 mb-3" />
            <p className="text-lg font-medium">Start a conversation with Rex</p>
            <p className="text-sm mt-1">Test his sales and customer service skills</p>
          </div>
        ) : (
          <div className="space-y-4 max-w-3xl mx-auto">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex space-x-3 max-w-2xl ${message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    message.role === 'user' ? 'bg-gray-200' : 'bg-gradient-to-br from-blue-500 to-purple-600'
                  }`}>
                    {message.role === 'user' ? (
                      <User className="h-5 w-5 text-gray-600" />
                    ) : (
                      <Bot className="h-5 w-5 text-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className={`rounded-lg px-4 py-2 ${
                      message.role === 'user' 
                        ? 'bg-purple-600 text-white' 
                        : 'bg-white border border-gray-200'
                    }`}>
                      <p className="text-sm">{message.content}</p>
                    </div>
                    {message.insights && showInsights && (
                      <div className="mt-2 text-xs text-gray-500 bg-gray-50 rounded-md px-3 py-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div><span className="font-medium">Sentiment:</span> {message.insights.sentiment}</div>
                          <div><span className="font-medium">Intent:</span> {message.insights.intent}</div>
                          <div><span className="font-medium">Stage:</span> {message.insights.stage}</div>
                          <div><span className="font-medium">Next:</span> {message.insights.next_action}</div>
                        </div>
                      </div>
                    )}
                    <div className="mt-1 text-xs text-gray-400">
                      {message.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="flex space-x-3 max-w-2xl">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                    <Bot className="h-5 w-5 text-white" />
                  </div>
                  <div className="bg-white border border-gray-200 rounded-lg px-4 py-2">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Conversation Summary */}
      {conversationSummary && (
        <div className="bg-gray-50 border-t px-6 py-3">
          <div className="flex items-center justify-between text-xs text-gray-600">
            <div className="flex items-center space-x-4">
              <span>Messages: {conversationSummary.message_count}</span>
              <span>Stage: <span className="font-medium">{conversationSummary.current_stage}</span></span>
              <span>Duration: {conversationSummary.duration}</span>
            </div>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="bg-white border-t px-6 py-4">
        <div className="flex space-x-3 max-w-3xl mx-auto">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message to test Rex..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            disabled={isLoading}
          />
          <button
            onClick={sendMessage}
            disabled={isLoading || !inputMessage.trim()}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <Send className="h-4 w-4" />
            <span>Send</span>
          </button>
        </div>
      </div>
    </div>
  );
}